import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AgentPage from './AgentPage';
import AgentPageV2 from './AgentPageV2';
import AgentPageV3 from './AgentPageV3';
import AgentObservatoryHome from './AgentObservatoryHome';
import LLMTracingPage from './LLMTracingPage';
import HCCCodesPage from './HCCCodesPage';
import RuleEditorPage from './RuleEditorPage';
import AgentHCCHome from './AgentHCCHome';
import WorkQueuePage from './WorkQueuePage';
import HCCReviewPage from './HCCReviewPage';
import AgentSpaceHome from './AgentSpaceHome';


import HCCConfigHome from './HCCConfigHome';
import SectionDefinitionsEditor from './SectionDefinitionsEditor.jsx';
import ChronicConditionsConfig from './ChronicConditionsConfig.jsx';
import FocusedConditionsConfig from './FocusedConditionsConfig.jsx';
import CodeCorrectionRules from './CodeCorrectionRules.jsx';
import CorroborationRules from './CorroborationRules.jsx';
import EvidenceHintsConfig from './EvidenceHintsConfig.jsx';
import SpecificityTargetsConfig from './SpecificityTargetsConfig.jsx';
import HCCRuntimeConfig from './HCCRuntimeConfig.jsx';
import HCCAICodeReview from './HCCAICodeReview.jsx';
import ChartLifecyclePage from './ChartLifecyclePage.jsx';

import HCCObservabilityHome from './HCCObservabilityHome.jsx';
import HCCTracingPage from './HCCTracingPage.jsx';

/**
 * AppRouter - Main routing component for the application
 * 
 * @param {Object} props
 * @param {boolean} props.isLoggedIn - Whether the user is logged in
 * @param {string} props.username - Current user's username
 * @param {string} props.sessionId - Current session ID
 * @param {string} props.userRole - Current user's role
 * @param {Array} props.userFeatures - User's available features/tabs
 * @param {string} props.defaultTab - User's default tab after login
 * @param {Array} props.tabOrder - Order of tabs to display (from config)
 * @param {function} props.onLogout - Logout handler function
 */
const AppRouter = ({ 
  isLoggedIn, 
  username, 
  sessionId, 
  userRole, 
  userFeatures, 
  defaultTab = 'HUB', 
  tabOrder = ['HUB', 'RCM', 'COLAB', 'PROJECT', 'MCP', 'COUNSEL', 'OBSERVE'],
  onLogout 
}) => {
  
  /**
   * Helper function to map tab name to route path
   * @param {string} tab - Tab name (e.g., 'HUB', 'COLAB', 'RCM')
   * @returns {string} - Route path
   */
  const getRouteForTab = (tab) => {
    const routes = {
      'HUB': '/agent-hub',
      'RCM': '/rcm',
      'COLAB': '/agent-colab',
      'MCP': '/mcp',
      'COUNSEL': '/counsel',
      'PROJECT': '/projects',
      'OBSERVE': '/observe',
      'HCC': '/hcc-home',
      'RAI_BUILDER': '/rai-builder-home',
      'RAI_DASHBOARD': '/rai-dashboard-home',
      'HCC_CODER_REQUESTED_REVIEW': '/hcc-review',  
      'MANAGE_CHARTS': '/manage-charts',
      "TRACE": '/trace',            
      };
    return routes[tab] || '/agent-hub';
  };

  /**
   * Get the default route based on defaultTab
   * Validates that defaultTab is in userFeatures, falls back to first available feature
   * @returns {string} - Default route path
   */
  const getDefaultRoute = () => {
    // First, check if defaultTab is valid and user has access
    if (defaultTab && userFeatures && userFeatures.includes(defaultTab)) {
      console.log(`Using default tab: ${defaultTab} -> ${getRouteForTab(defaultTab)}`);
      return getRouteForTab(defaultTab);
    }
    
    // Fallback: use first available feature
    if (userFeatures && userFeatures.length > 0) {
      console.log(`Falling back to first feature: ${userFeatures[0]} -> ${getRouteForTab(userFeatures[0])}`);
      return getRouteForTab(userFeatures[0]);
    }
    
    // Ultimate fallback
    console.log('Using ultimate fallback: /agent-hub');
    return '/agent-hub';
  };

  // Common props for protected components
  const commonProps = {
    username,
    sessionId,
    userRole,
    userFeatures,
    tabOrder,
    onLogout
  };
  
  return (
    <Routes>
      {/* Root path - Redirect to default tab */}
      <Route 
        path="/" 
        element={<Navigate to={getDefaultRoute()} replace />} 
      />

      {/* Agent Hub route */}
      <Route 
        path="/agent-hub" 
        element={<AgentSpaceHome {...commonProps} />} 
      />

      {/* HCC Observatory Dashboard route */}
      <Route 
        path="/observe" 
        element={<HCCObservabilityHome {...commonProps} />} 
      />

      {/* HCC LLM Tracing route */}
      <Route 
        path="/trace" 
        element={<HCCTracingPage {...commonProps} />} 
      />


     {/* LLM Tracing route */}
      <Route 
        path="/observe/llm-tracing" 
        element={<LLMTracingPage {...commonProps} />} 
      />


      {/* Agent route */}
      <Route 
        path="/agent/:agentId" 
        element={<AgentPage {...commonProps} />} 
      />

      {/* Agents using ChatComponentV2 route */}
      <Route 
        path="/agent/v2/:agentId" 
        element={<AgentPageV2 {...commonProps} />} 
      />



      {/* HUB Agents using ChatComponentV2 route */}
      <Route 
        path="/hub/agent/v2/:agentId" 
        element={<AgentPageV2 {...commonProps} />} 
      />


      {/* HUB Agent route (legacy V1) */}
      <Route 
        path="/hub/agent/:agentId" 
        element={<AgentPage {...commonProps} />} 
      />


      {/* HUB Agents using V3 route */}
      <Route 
        path="/hub/agent/v3/:agentId" 
        element={<AgentPageV3 {...commonProps} />} 
      />

      <Route 
        path="/hcc" 
        element={<HCCCodesPage {...commonProps} />} 
      />

      <Route 
        path="/hcc-rules" 
        element={<RuleEditorPage {...commonProps} />} 
      />


      {/* HC Routes */}

      <Route 
        path="/hcc-home" 
        element={
          <AgentHCCHome {...commonProps} />
        } 
      />


      <Route 
        path="/hcc-config" 
        element={<HCCConfigHome {...commonProps} />} 
      />

      <Route 
        path="/hcc-config/sections" 
        element={<SectionDefinitionsEditor {...commonProps} />} 
      />


      <Route 
        path="/hcc-config/chronic" 
        element={<ChronicConditionsConfig {...commonProps} />} 
      />


      <Route 
        path="/hcc-config/chronic-focused" 
        element={<FocusedConditionsConfig {...commonProps} />} 
      />

      <Route 
        path="/hcc-config/code-corrections" 
        element={<CodeCorrectionRules {...commonProps} />} 
      />

      <Route 
        path="/hcc-config/corroborations" 
        element={<CorroborationRules {...commonProps} />} 
      />

      <Route 
        path="/hcc-config/specificity" 
        element={<SpecificityTargetsConfig {...commonProps} />} 
      />


      <Route 
        path="/hcc-config/evidence-search" 
        element={<EvidenceHintsConfig {...commonProps} />} 
      />


      <Route 
        path="/hcc-config/runtime" 
        element={<HCCRuntimeConfig {...commonProps} />} 
      />

      {/* page to review AI generated codes referred by coders for review */}
      <Route path="/hcc-review" element={<HCCAICodeReview {...commonProps} />} />


      <Route 
        path="/manage-charts" 
        element={<ChartLifecyclePage {...commonProps} />} 
      />


      <Route 
        path="/hcc-home/review/:chartId" 
        element={<HCCReviewPage {...commonProps} />} 
      />

      <Route
        path="/my-inventory/:workType"
        element={<WorkQueuePage {...commonProps} />}
      />

      <Route path="/hcc-home/agent/:agentId" element={<AgentPage {...commonProps} />} />
      
      <Route path="/hcc-home/agent/v2/:agentId" element={<AgentPageV2 {...commonProps} />} />
      
      <Route path="/hcc-home/agent/v3/:agentId" element={<AgentPageV3 {...commonProps} />} />






      {/* Default route redirects to user's default tab */}
      <Route 
        path="*" 
        element={<Navigate to={getDefaultRoute()} replace />} 
      />
    </Routes>
  );
};

export default AppRouter;